import { useState } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button, SelectOption, TextField } from '@tryhackme/thm-ui-components';
import { NodeMachineStatus, NetworkInstanceType } from 'src/common/enums/networks';
import { DeleteModal } from 'src/common/delete-modal';
import { useFormContext, useFieldArray, Controller } from 'src/common/form';
import { NodeImages } from '../node-images';
import { StyledNodesContainer, StyledCancelIconHeader, StyledElementHeader, StyledFormLabel, StyledFormToggle, StyledElementTitle, StyledFontAwesomeIcon, StyledDeleteIcon } from '../../networks.styles';
import { StyledAMIField, StyledElementsContainer, StyledGridContainer, StyledIdField, StyledInstanceIdField, StyledInstanceTypeField, StyledLabelField, StyledLevelField, StyledMachineUrlField, StyledPrivateIpField, StyledSecurityGroupField, StyledStatusField, StyledSubtitle, StyledSubtitleContainer } from './nodes.styles';
export const NetworkNodes = () => {
  const {
    control,
    setValue,
    register
  } = useFormContext();
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'nodes'
  });
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const machineStatusOptions: SelectOption[] = [{
    id: '0',
    label: 'Machine',
    value: NodeMachineStatus.MACHINE
  }, {
    id: '1',
    label: 'Icon Only',
    value: NodeMachineStatus.ICONONLY
  }];
  const handleStatusChange = (name: `nodes.${number}.machine.status`, value?: SelectOption) => {
    if (value) {
      const valueToSet = (value.value as NodeMachineStatus);
      setValue(name, valueToSet);
    }
  };
  return <StyledNodesContainer data-testid="nodes" data-sentry-element="StyledNodesContainer" data-sentry-component="NetworkNodes" data-sentry-source-file="nodes.tsx">
      <StyledSubtitleContainer data-sentry-element="StyledSubtitleContainer" data-sentry-source-file="nodes.tsx">
        <StyledSubtitle data-sentry-element="StyledSubtitle" data-sentry-source-file="nodes.tsx">Nodes</StyledSubtitle>
        <Button variant="solid" color="add" aria-label="add" onClick={() => append({
        id: 0,
        level: 0,
        image: '',
        label: '',
        change: [],
        machine: {
          status: NodeMachineStatus.MACHINE,
          ami: '',
          privateIp: '',
          sourceDestChecks: false,
          instanceId: '',
          instanceType: NetworkInstanceType.EMPTY,
          sg: '',
          reset: new Date()
        },
        showOnMap: false
      })} data-sentry-element="Button" data-sentry-source-file="nodes.tsx">
          <StyledFontAwesomeIcon icon={faPlus} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="nodes.tsx" />
          Add Node
        </Button>
      </StyledSubtitleContainer>
      {fields?.map((item, index) => <StyledElementsContainer key={item.id}>
          <StyledCancelIconHeader>
            <Button variant="text" color="secondary" aria-label="Delete node" onClick={() => {
          setActiveIndex(index);
          setDeleteModalOpen(true);
        }}>
              <StyledDeleteIcon icon={faXmark} />
            </Button>
          </StyledCancelIconHeader>
          <StyledElementHeader>
            <StyledElementTitle>{`Node ${index + 1}`}</StyledElementTitle>
            <Controller name={`nodes.${index}.showOnMap`} control={control} render={({
          field: {
            value,
            onChange,
            ref,
            ...rest
          }
        }) => <StyledFormLabel>
                  <StyledFormToggle {...rest} checked={!!value} onCheckedChange={onChange} innerRef={ref} aria-label="show-on-map" />
                  Show on map
                </StyledFormLabel>} />
          </StyledElementHeader>
          <StyledGridContainer>
            <StyledIdField>
              <TextField {...register(`nodes.${index}.id`)} variant="input" label="ID" aria-label="id" />
            </StyledIdField>
            <StyledMachineUrlField>
              <TextField {...register(`nodes.${index}.image`)} id={`nodes.${index}.image`} variant="input" label="Machine Image URL" />
            </StyledMachineUrlField>
            <StyledLabelField>
              <TextField {...register(`nodes.${index}.label`)} id={`nodes.${index}.label`} variant="input" label="Label" />
            </StyledLabelField>
            <StyledLevelField>
              <TextField {...register(`nodes.${index}.level`)} id={`nodes.${index}.level`} variant="input" label="Level" />
            </StyledLevelField>
            <StyledSecurityGroupField>
              <TextField {...register(`nodes.${index}.machine.sg`)} id={`nodes.${index}.machine.sg`} variant="input" label="Security group" />
            </StyledSecurityGroupField>

            <StyledStatusField>
              <Controller name={`nodes.${index}.machine.status`} control={control} render={({
            field: {
              ref,
              value,
              onChange,
              name,
              ...rest
            }
          }) => <TextField isMulti={false} isSearchable={false} variant="select" options={machineStatusOptions} selected={machineStatusOptions.find(option => option.value === value)} handleSelection={selectedValue => handleStatusChange(name, selectedValue)} label="Status" innerRef={ref} id={`nodes.${index}.machine.status`} {...rest} />} />
            </StyledStatusField>
            <StyledAMIField>
              <TextField {...register(`nodes.${index}.machine.ami`)} id={`nodes.${index}.machine.ami`} variant="input" label="AMI" />
            </StyledAMIField>
            <StyledInstanceIdField>
              <TextField {...register(`nodes.${index}.machine.instanceId`)} id={`nodes.${index}.machine.instanceId`} variant="input" label="Instance ID" />
            </StyledInstanceIdField>
            <StyledPrivateIpField>
              <TextField {...register(`nodes.${index}.machine.privateIp`)} id={`nodes.${index}.machine.privateIp`} variant="input" label="Private IP" />
            </StyledPrivateIpField>
            <StyledInstanceTypeField>
              <TextField {...register(`nodes.${index}.machine.instanceType`)} id={`nodes.${index}.machine.instanceType`} variant="input" label="Instance type" />
            </StyledInstanceTypeField>
          </StyledGridContainer>
          <NodeImages nestIndex={index} {...{
        control,
        register
      }} />
        </StyledElementsContainer>)}
      <DeleteModal open={deleteModalOpen} onOpenChange={setDeleteModalOpen} modalTitleText="Delete Node" modalBodyText="Are you sure you want to delete this node?" activeIndex={activeIndex} handleDeleteAction={(activeInd: number) => remove(activeInd)} data-sentry-element="DeleteModal" data-sentry-source-file="nodes.tsx" />
    </StyledNodesContainer>;
};